import { useState, type FormEvent, type ReactNode } from "react";
import type { CrmAccount } from "@shared/modules/crm";
import { api, ApiError } from "../../../core/lib/api";
import { Button } from "../../../core/components/ui/button";
import {
  Field,
  Input,
  Select,
  Textarea,
  type FieldControlProps,
} from "../../../core/components/ui/input";
import { formatDateTime } from "../../../core/lib/format";
import { useToastStore } from "../../../core/stores/toast";
import {
  centsToInput,
  dateTimeLocalToIso,
  formatCents,
  parseAmountToCents,
  toDateTimeLocalValue,
} from "../lib/crm";

export type EditableFieldType =
  | "text"
  | "email"
  | "tel"
  | "url"
  | "textarea"
  | "select"
  | "money"
  | "percent"
  | "date";

export interface EditableField {
  key: keyof CrmAccount & string;
  label: string;
  type: EditableFieldType;
  /** Pour un type "select". Ajouter une option de valeur "" rend le champ effaçable. */
  options?: Array<{ value: string; label: string }>;
  hint?: string;
  placeholder?: string;
  /** Colonne non nullable côté API (le pays) : on refuse de la vider plutôt que d'encaisser un 400. */
  required?: boolean;
}

interface EditableSectionProps {
  title: string;
  fields: EditableField[];
  account: CrmAccount;
  onSaved: () => void;
}

interface DraftState {
  values: Record<string, string>;
  /** Photo de la saisie à l'ouverture : le PATCH ne transporte que ce qui en diffère. */
  initial: Record<string, string>;
}

type PayloadValue = string | number | null;
type Conversion = { ok: true; value: PayloadValue } | { ok: false; error: string };

// Une valeur absente se voit : c'est ce qu'il reste à remplir.
const EMPTY = <span className="crm-def__empty">—</span>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Bloc de la fiche qui s'édite seul : lecture par défaut, formulaire au clic. */
export function EditableSection({ title, fields, account, onSaved }: EditableSectionProps) {
  const push = useToastStore((s) => s.push);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const open = () => {
    const values: Record<string, string> = {};
    for (const field of fields) values[field.key] = toInput(field, account);
    setDraft({ values, initial: { ...values } });
    setErrors({});
  };

  const close = () => {
    setDraft(null);
    setErrors({});
  };

  const set = (key: string, value: string) => {
    setDraft((prev) => (prev ? { ...prev, values: { ...prev.values, [key]: value } } : prev));
    // Corriger la saisie doit faire disparaître le reproche, pas attendre le prochain envoi.
    setErrors((prev) => (key in prev ? omit(prev, key) : prev));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft || saving) return;

    const payload: Record<string, PayloadValue> = {};
    const nextErrors: Record<string, string> = {};
    for (const field of fields) {
      const value = draft.values[field.key] ?? "";
      if (value === (draft.initial[field.key] ?? "")) continue;
      const converted = convert(field, value);
      if (converted.ok) payload[field.key] = converted.value;
      else nextErrors[field.key] = converted.error;
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    // Rien n'a bougé : inutile d'aller réveiller le serveur pour fermer un bloc.
    if (Object.keys(payload).length === 0) {
      close();
      return;
    }

    setSaving(true);
    try {
      await api(`/api/crm/accounts/${account.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      push("success", "Bloc enregistré", title);
      close();
      onSaved();
    } catch (err) {
      const forbidden = err instanceof ApiError && err.status === 403;
      push(
        "danger",
        "Échec de l'enregistrement",
        forbidden ? "Le CRM est réservé aux administrateurs." : "Vérifiez les champs saisis.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!draft) {
    return (
      <section className="crm-section">
        <div className="crm-section__head">
          <h2 className="crm-section__title">{title}</h2>
          <Button variant="ghost" size="sm" className="crm-section__edit" onClick={open}>
            Modifier
          </Button>
        </div>
        <dl className="crm-def">
          {fields.map((field) => (
            <div key={field.key} className="crm-def__row">
              <dt className="crm-def__label">{field.label}</dt>
              <dd
                className={`crm-def__value${field.type === "textarea" ? " crm-def__value--pre" : ""}`}
              >
                {readValue(field, account)}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    );
  }

  return (
    <section className="crm-section">
      <div className="crm-section__head">
        <h2 className="crm-section__title">{title}</h2>
      </div>
      <form
        className="crm-edit"
        onSubmit={submit}
        // Échap ferme le bloc : on édite au fil de l'eau, on doit pouvoir en sortir aussi vite.
        onKeyDown={(e) => {
          if (e.key === "Escape" && !saving) close();
        }}
      >
        <div className="crm-edit__grid">
          {fields.map((field) => (
            <div
              key={field.key}
              className={`crm-edit__cell${field.type === "textarea" ? " crm-edit__cell--wide" : ""}`}
            >
              <Field label={field.label} hint={field.hint} error={errors[field.key]}>
                <Control
                  field={field}
                  value={draft.values[field.key] ?? ""}
                  onChange={(v) => set(field.key, v)}
                />
              </Field>
            </div>
          ))}
        </div>
        <div className="crm-edit__actions">
          <Button type="button" variant="ghost" onClick={close} disabled={saving}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </form>
    </section>
  );
}

interface ControlProps extends FieldControlProps {
  field: EditableField;
  value: string;
  onChange: (value: string) => void;
}

// `attach` porte l'identifiant, aria-invalid et aria-describedby posés par
// `Field` : sans ce relais, le libellé et le message d'erreur du bloc édité
// resteraient de simples textes voisins du champ.
function Control({ field, value, onChange, ...attach }: ControlProps) {
  switch (field.type) {
    case "textarea":
      return (
        <Textarea
          {...attach}
          rows={5}
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "select":
      return (
        <Select {...attach} value={value} onChange={(e) => onChange(e.target.value)}>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      );
    case "date":
      return (
        <Input {...attach} type="date" value={value} onChange={(e) => onChange(e.target.value)} />
      );
    case "money":
      return (
        <Input
          {...attach}
          inputMode="decimal"
          value={value}
          placeholder={field.placeholder ?? "0,00"}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "percent":
      return (
        <Input
          {...attach}
          inputMode="decimal"
          value={value}
          placeholder={field.placeholder ?? "0"}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "email":
      return (
        <Input
          {...attach}
          type="email"
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "tel":
      return (
        <Input
          {...attach}
          type="tel"
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    // Un site saisi « exemple.com » est légitime : type="url" le refuserait à l'envoi.
    default:
      return (
        <Input
          {...attach}
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

function omit(errors: Record<string, string>, key: string): Record<string, string> {
  const next = { ...errors };
  delete next[key];
  return next;
}

/** Valeur du dossier -> chaîne éditable. */
function toInput(field: EditableField, account: CrmAccount): string {
  const raw = account[field.key];
  switch (field.type) {
    case "money":
      return centsToInput(typeof raw === "number" ? raw : 0);
    case "percent":
      return typeof raw === "number" && raw !== 0 ? String(raw) : "";
    case "date":
      return isoToDateInput(raw);
    default:
      return typeof raw === "string" ? raw : "";
  }
}

/** Chaîne éditable -> valeur envoyée à l'API, ou message d'erreur affiché sous le champ. */
function convert(field: EditableField, input: string): Conversion {
  const text = input.trim();

  if (field.type === "money") {
    // Les montants et le taux ne sont pas nullables en base : vider revient à zéro.
    if (!text) return { ok: true, value: 0 };
    const cents = parseAmountToCents(text);
    if (cents === null || cents < 0) {
      return { ok: false, error: "Montant invalide — par exemple 1 200,00." };
    }
    return { ok: true, value: cents };
  }

  if (field.type === "percent") {
    if (!text) return { ok: true, value: 0 };
    const pct = Number(text.replace(",", "."));
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      return { ok: false, error: "Pourcentage attendu entre 0 et 100." };
    }
    return { ok: true, value: pct };
  }

  if (field.type === "date") {
    if (!text) return { ok: true, value: null };
    const iso = dateInputToIso(text);
    return iso ? { ok: true, value: iso } : { ok: false, error: "Date invalide." };
  }

  if (!text) {
    return field.required
      ? { ok: false, error: "Ce champ ne peut pas rester vide." }
      : { ok: true, value: null };
  }

  if (field.type === "email" && !EMAIL_RE.test(text)) {
    return { ok: false, error: "Courriel invalide." };
  }

  return { ok: true, value: text };
}

function readValue(field: EditableField, account: CrmAccount): ReactNode {
  const raw = account[field.key];

  if (field.type === "money") {
    const cents = typeof raw === "number" ? raw : 0;
    return cents === 0 ? EMPTY : formatCents(cents, account.currency);
  }
  if (field.type === "percent") {
    const pct = typeof raw === "number" ? raw : 0;
    return pct === 0 ? EMPTY : `${pct} %`;
  }
  if (field.type === "date") {
    const formatted = formatDateTime(raw, { dateStyle: "long" });
    return formatted === "—" ? EMPTY : formatted;
  }

  const text = typeof raw === "string" ? raw.trim() : "";
  if (!text) return EMPTY;

  switch (field.type) {
    case "select":
      return field.options?.find((o) => o.value === text)?.label ?? text;
    case "url":
      return (
        <a href={toHref(text)} target="_blank" rel="noreferrer">
          {text}
        </a>
      );
    case "email":
      return <a href={`mailto:${text}`}>{text}</a>;
    case "tel":
      return <a href={`tel:${text}`}>{text}</a>;
    default:
      return text;
  }
}

// <input type="date"> ne transporte pas d'heure : on ancre à minuit local pour
// qu'une date de contrat ne recule pas d'un jour une fois sérialisée en UTC.
function dateInputToIso(value: string): string | null {
  return dateTimeLocalToIso(`${value}T00:00`);
}

function isoToDateInput(value: string | number | null): string {
  return toDateTimeLocalValue(value).slice(0, 10);
}

// Les sites sont saisis à la main : « exemple.com » doit rester un lien absolu.
function toHref(website: string): string {
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
}
